import { Box, LinearProgress, Table, TableBody, TableHead } from '@mui/material';
import React from 'react';
import { LayoutRows2 } from '../../LayoutRows2';
import { handleErrorSnackbar } from '../../SnackbarHelper';
import { Head } from './Head';
import { Pagination } from './Pagination';
import { Row } from './Row';
import { RowMenu } from './RowMenu';
export const Content = React.memo(ContentComponent);
function ContentComponent(props) {
    const [sort, setSort] = React.useState(() => getSort(getInitialSortColumn(props.columns, props.initialSortColumnName), props.initialSortColumnDesc));
    const [sortColumnName, setSortColumnName] = React.useState(() => { var _a; return (_a = getInitialSortColumn(props.columns, props.initialSortColumnName)) === null || _a === void 0 ? void 0 : _a.name; });
    const [sortDesc, setSortDesc] = React.useState(() => { var _a; return (_a = props.initialSortColumnDesc) !== null && _a !== void 0 ? _a : false; });
    const [rows, setRows] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(50);
    const [total, setTotal] = React.useState(0);
    const [loading, setLoading] = React.useState(false);
    const suppressReload = React.useRef(false);
    const currentPromise = React.useRef();
    const timeoutHandle = React.useRef();
    React.useEffect(() => {
        const initialSortColumn = getInitialSortColumn(props.columns, props.initialSortColumnName);
        if (initialSortColumn && sort && !sort.length) {
            setSort(getSort(initialSortColumn, props.initialSortColumnDesc));
            setSortColumnName(initialSortColumn.name);
        }
    }, [props.columns, props.initialSortColumnName, props.initialSortColumnDesc, sort]);
    const reload = React.useCallback(() => {
        if (!timeoutHandle.current) {
            timeoutHandle.current = window.setTimeout(() => setLoading(true), 500);
        }
        const listFor = (page) => props.service.list(props.appliedFilter, sort, rowsPerPage, page * rowsPerPage);
        const promise = listFor(page)
            .then((result) => {
            if (currentPromise.current !== promise) {
                return;
            }
            if (result.rows.length === 0 && page > 0 && result.total > 0) {
                const lastPage = Math.ceil(result.total / rowsPerPage) - 1;
                return listFor(lastPage).then((result) => {
                    suppressReload.current = true;
                    setPage(lastPage);
                    setRows(result.rows);
                    setTotal(result.total);
                });
            }
            else {
                setRows(result.rows);
                setTotal(result.total);
                return;
            }
        })
            .catch((error) => {
            if (currentPromise.current === promise) {
                handleErrorSnackbar(error);
            }
        })
            .finally(() => {
            if (currentPromise.current === promise) {
                window.clearTimeout(timeoutHandle.current);
                currentPromise.current = undefined;
                timeoutHandle.current = undefined;
                setLoading(false);
            }
        });
        currentPromise.current = promise;
    }, [props.service, props.appliedFilter, sort, page, rowsPerPage]);
    React.useEffect(() => {
        if (!suppressReload.current) {
            reload();
        }
        else {
            suppressReload.current = false;
        }
    }, [reload, props.reloadFlag]);
    const [menuEl, setMenuEl] = React.useState();
    const [menuRow, setMenuRow] = React.useState();
    const showMenu = React.useCallback((el, row) => {
        setMenuEl(el);
        setMenuRow(row);
    }, []);
    const closeMenu = React.useCallback(() => {
        setMenuEl(undefined);
        setMenuRow(undefined);
    }, []);
    const { multiSelectedRowIds, onMultiSelectChange } = props;
    const handleSelectAll = React.useCallback(() => {
        if (onMultiSelectChange) {
            const rowIds = rows.map((row) => props.service.id(row));
            if (getMultiSelectState(rowIds, multiSelectedRowIds) !== 2) {
                const newRowIds = multiSelectedRowIds ? [...multiSelectedRowIds] : [];
                for (const id of rowIds) {
                    if (!newRowIds.includes(id)) {
                        newRowIds.push(id);
                    }
                }
                onMultiSelectChange(newRowIds);
            }
            else if (multiSelectedRowIds) {
                onMultiSelectChange(multiSelectedRowIds.filter((id) => !rowIds.includes(id)));
            }
        }
    }, [props.service, multiSelectedRowIds, onMultiSelectChange, rows]);
    return (React.createElement(LayoutRows2, { row1: React.createElement(Box, { width: 1, height: 1, position: 'relative', "data-test": 'content' },
            loading && (React.createElement(Box, { sx: {
                    position: 'absolute',
                    top: 0,
                    right: 0,
                    left: 0,
                    zIndex: (theme) => theme.zIndex.mobileStepper - 1,
                }, "data-test": 'loader' },
                React.createElement(LinearProgress, null))),
            React.createElement(Box, { width: 1, height: 1, overflow: 'auto' },
                React.createElement(Table, { stickyHeader: true, style: { tableLayout: 'fixed' } },
                    React.createElement(TableHead, null,
                        React.createElement(Head, { sortColumnName: sortColumnName, sortDesc: sortDesc, columns: props.columns, multiSelect: props.multiSelect, multiSelectState: getMultiSelectState(rows.map((row) => props.service.id(row)), multiSelectedRowIds), menu: !!props.rowMenuItems, onToggleSelectAll: handleSelectAll, onChangeSort: (column, desc) => {
                                setSort(getSort(column, desc));
                                setSortColumnName(column.name);
                                setSortDesc(desc);
                            } })),
                    React.createElement(TableBody, null, rows.map((row) => {
                        const id = props.service.id(row);
                        return (React.createElement(Row, { key: id, columns: props.columns, id: id, multiSelect: props.multiSelect, row: row, selected: props.selectedRowId === id, multiSelectedRowIds: props.multiSelectedRowIds, onSelectRow: props.onSelectRow, onMultiSelectChange: props.onMultiSelectChange, onMenu: props.rowMenuItems ? showMenu : undefined }));
                    })))),
            props.rowMenuItems && React.createElement(RowMenu, { el: menuEl, row: menuRow, rowMenuItems: props.rowMenuItems, onClose: closeMenu })), row2: React.createElement(Pagination, { total: total, page: page, rowsPerPage: rowsPerPage, initialRowsPerPage: props.initialRowsPerPage, onChange: (page, rowsPerPage) => {
                setPage(page);
                setRowsPerPage(rowsPerPage);
            } }), fullHeightRow: 'row1' }));
}
function getInitialSortColumn(columns, initialSortColumnName) {
    if (initialSortColumnName) {
        return columns.find((c) => c.sortable && c.name === initialSortColumnName);
    }
    else {
        return columns.find((c) => c.sortable);
    }
}
function getSort(column, desc) {
    if (column) {
        if (column.sort) {
            return column.sort(desc !== null && desc !== void 0 ? desc : false);
        }
        else {
            return [{ field: column.name, desc: desc !== null && desc !== void 0 ? desc : false }];
        }
    }
    else {
        return [];
    }
}
function getMultiSelectState(rowIds, selectedIds) {
    let multiSelectState = 0;
    let allRowsSelected = true;
    for (const rowId of rowIds) {
        if (selectedIds && selectedIds.includes(rowId)) {
            multiSelectState = 1;
        }
        else {
            allRowsSelected = false;
        }
    }
    if (allRowsSelected && rowIds.length > 0) {
        multiSelectState = 2;
    }
    return multiSelectState;
}
